import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

type Props = {
  planId: string;
  clientId?: string;
  deferSave?: boolean;
  registerSaveHandler?: (fn: () => Promise<void>) => void;
  registerRowsProvider?: (
    fn: () => Array<{
      planRateId?: number | null;
      contributionType: "tier_rates" | "age_rates";
      contributionTypeValue: string;
      payer?: "employer" | "employee";
      percentage?: number | string | null;
      nominalAmount?: number | string | null;
      maxAmount?: number | string | null;
    }>
  ) => void;
  payerOverride?: "employer" | "employee";
  unstyled?: boolean;
};

export default function PerRateContributions({
  planId,
  clientId,
  deferSave,
  registerSaveHandler,
  registerRowsProvider,
  payerOverride,
  unstyled,
}: Props) {
  const { toast } = useToast();

  // Helpers for ordering and display
  const parseStart = (label: string) => {
    const start = label.toString().split("-")[0].replace("+", "");
    const n = parseInt(start, 10);
    return isNaN(n) ? Number.MAX_SAFE_INTEGER : n;
  };
  const normalizeDisplayLabel = (label: string) =>
    label.replace(/^(\d+)\s*-\s*\1$/, "$1").replace(/^(\d+)-99$/, "$1+");

  const { data: planRates } = useQuery({
    queryKey: clientId
      ? ["/api/plan-rates/", planId, clientId]
      : ["/api/plan-rates/", planId],
    queryFn: async () => {
      const url = clientId
        ? `/api/plan-rates/${planId}?clientId=${clientId}`
        : `/api/plan-rates/${planId}`;
      const res = await fetch(url);
      if (!res.ok) return null;
      return res.json();
    },
  });

  // Load contributions to prefill per-rate values if already saved
  const { data: allContributions = [] } = useQuery({
    queryKey: ["/api/plan-contributions"],
    queryFn: async () => {
      const res = await fetch(`/api/plan-contributions`);
      if (!res.ok) return [];
      return res.json();
    },
  });

  const structure = planRates?.rateStructure as any;
  const rates = (planRates?.rates as any[]) || [];

  const [globalType, setGlobalType] = useState<"percentage" | "dollar">(
    "percentage"
  );
  const [globalAmount, setGlobalAmount] = useState<string>("");
  const [rows, setRows] = useState<
    Array<{ key: string; label: string; monthly: string; type: "tier" | "age" }>
  >([]);
  const [values, setValues] = useState<
    Record<
      string,
      {
        type: "percentage" | "dollar";
        amount: string;
        payer?: "employer" | "employee";
        percent?: string;
      }
    >
  >({});

  useEffect(() => {
    if (!structure) return;
    const planIdNum = parseInt(planId);
    const rowsForPlan = Array.isArray(allContributions)
      ? (allContributions as any[]).filter((r) => r.planId === planIdNum)
      : [];
    if (structure.rateStructureType === "tier_rates") {
      const next = rates
        .map((r: any) => ({
          key: `tier:${r.coverageTier}`,
          label: String(r.coverageTier).replace(/_/g, " "),
          monthly: String(r.monthlyRate ?? ""),
          type: "tier" as const,
        }))
        .sort((a: any, b: any) => {
          const order = [
            "employee only",
            "employee spouse",
            "employee children",
            "family",
          ];
          const ai = order.indexOf(a.label.toLowerCase());
          const bi = order.indexOf(b.label.toLowerCase());
          if (ai === -1 && bi === -1) return a.label.localeCompare(b.label);
          if (ai === -1) return 1;
          if (bi === -1) return -1;
          return ai - bi;
        });
      setRows(next);
      const v: Record<
        string,
        {
          type: "percentage" | "dollar";
          amount: string;
          payer?: "employer" | "employee";
          percent?: string;
        }
      > = {};
      next.forEach((n) => {
        const base = values[n.key] || { type: "percentage", amount: "" };
        const contrib = rowsForPlan.find(
          (c: any) =>
            c.contributionType === "tier_rates" &&
            (c.contributionTypeValue || "").toLowerCase() ===
              n.label.toLowerCase()
        );
        const typeFromDb =
          contrib && contrib.percentage != null
            ? "percentage"
            : contrib && contrib.nominalAmount != null
            ? "dollar"
            : base.type;
        const amountFromDb =
          contrib && contrib.percentage != null
            ? String(contrib.percentage)
            : contrib && contrib.nominalAmount != null
            ? String(contrib.nominalAmount)
            : base.amount;
        v[n.key] = {
          type: typeFromDb,
          amount: amountFromDb,
          payer: contrib?.payer || base.payer,
          percent:
            contrib?.percentage != null
              ? String(contrib.percentage)
              : base.percent,
        } as any;
      });
      setValues(v);
    } else {
      const next = rates
        .filter((r: any) => r.ageBand != null)
        .map((r: any) => ({
          key: `age:${r.ageBand}`,
          label: String(r.ageBand),
          monthly: String(r.monthlyRate ?? ""),
          type: "age" as const,
        }))
        .sort((a: any, b: any) => parseStart(a.label) - parseStart(b.label));
      setRows(next);
      const v: Record<
        string,
        {
          type: "percentage" | "dollar";
          amount: string;
          payer?: "employer" | "employee";
          percent?: string;
        }
      > = {};
      next.forEach((n) => {
        const base = values[n.key] || { type: "percentage", amount: "" };
        const contrib = rowsForPlan.find(
          (c: any) =>
            c.contributionType === "age_rates" &&
            String(c.contributionTypeValue) === n.label
        );
        const typeFromDb =
          contrib && contrib.percentage != null
            ? "percentage"
            : contrib && contrib.nominalAmount != null
            ? "dollar"
            : base.type;
        const amountFromDb =
          contrib && contrib.percentage != null
            ? String(contrib.percentage)
            : contrib && contrib.nominalAmount != null
            ? String(contrib.nominalAmount)
            : base.amount;
        v[n.key] = {
          type: typeFromDb,
          amount: amountFromDb,
          payer: contrib?.payer || base.payer,
          percent:
            contrib?.percentage != null
              ? String(contrib.percentage)
              : base.percent,
        } as any;
      });
      setValues(v);
    }
  }, [structure, rates, allContributions, planId]);

  const applyGlobal = () => {
    const v: Record<string, { type: "percentage" | "dollar"; amount: string }> =
      {};
    rows.forEach(
      (r) => (v[r.key] = { type: globalType, amount: globalAmount })
    );
    setValues(v);
  };

  const onSave = async () => {
    try {
      if (!structure) return;
      if (structure.rateStructureType === "tier_rates") {
        const payload = {
          clientId: clientId ? parseInt(clientId) : null,
          contributionSource:
            values[rows.find((r) => r.type === "tier")?.key || ""]?.payer ||
            "employer",
          rows: rows
            .filter((r) => r.type === "tier")
            .map((r) => ({
              coverageTier: r.label.toLowerCase().replace(/\s+/g, "_"),
              employeeContributionType: values[r.key]?.type || "percentage",
              amount: values[r.key]?.amount || "0",
              contributionPayer: values[r.key]?.payer,
              percent: values[r.key]?.percent,
            })),
        };
        const res = await fetch(`/api/plan-tier-contributions/bulk/${planId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error("Failed to save tier contributions");
      } else {
        const payload = {
          clientId: clientId ? parseInt(clientId) : null,
          contributionSource:
            globalType === "percentage" || globalType === "dollar"
              ? values[rows.find((r) => r.type === "age")?.key || ""]?.payer ||
                "employer"
              : "employer",
          rows: rows
            .filter((r) => r.type === "age")
            .map((r) => ({
              ageBand: r.label,
              employeeContributionType: values[r.key]?.type || "percentage",
              amount: values[r.key]?.amount || "0",
              // omit payer/percent to allow backend to derive for age rates
            })),
        };
        const res = await fetch(
          `/api/plan-table-contributions/bulk/${planId}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          }
        );
        if (!res.ok) throw new Error("Failed to save table contributions");
      }
      toast({
        title: "Contributions saved",
        description: "Per-rate contributions updated.",
      });
    } catch (e: any) {
      toast({
        title: "Error",
        description: e?.message || "Failed to save",
        variant: "destructive",
      });
      throw e;
    }
  };

  // Provide rows back to parent so it can save all at once (and delete first)
  const provideRows = () => {
    if (!structure) return [] as any[];
    if (structure.rateStructureType === "tier_rates") {
      const rowsOut = rows
        .filter((r) => r.type === "tier")
        .map((r) => {
          const tierKey = r.label.toLowerCase().replace(/\s+/g, "_");
          const planRate = rates.find(
            (x: any) => String(x.coverageTier).replace(/_/g, " ") === r.label
          );
          const val = values[r.key] || { type: "percentage", amount: "" };
          if (!val.amount) return null;
          return {
            planRateId: planRate?.id ?? null,
            contributionType: "tier_rates" as const,
            contributionTypeValue: tierKey,
            payer: val.payer || payerOverride || "employer",
            percentage: val.type === "percentage" ? val.amount : null,
            nominalAmount: val.type === "dollar" ? val.amount : null,
          };
        })
        .filter((v): v is NonNullable<typeof v> => v !== null);
      return rowsOut as any[];
    }
    const rowsOut = rows
      .filter((r) => r.type === "age")
      .map((r) => {
        const planRate = rates.find((x: any) => String(x.ageBand) === r.label);
        const val = values[r.key] || { type: "percentage", amount: "" };
        if (!val.amount) return null;
        return {
          planRateId: planRate?.id ?? null,
          contributionType: "age_rates" as const,
          contributionTypeValue: r.label,
          payer: payerOverride || "employer",
          percentage: val.type === "percentage" ? val.amount : null,
          nominalAmount: val.type === "dollar" ? val.amount : null,
        };
      })
      .filter((v): v is NonNullable<typeof v> => v !== null);
    return rowsOut as any[];
  };

  useEffect(() => {
    if (registerRowsProvider) {
      registerRowsProvider(provideRows);
    }
  }, [registerRowsProvider, rows, values, structure, rates, payerOverride]);

  useEffect(() => {
    if (deferSave && registerSaveHandler) {
      registerSaveHandler(onSave);
    }
  }, [deferSave, registerSaveHandler, rows, values, structure]);

  const title = useMemo(() => {
    if (!structure) return "Per-Rate Contributions";
    return structure.rateStructureType === "tier_rates"
      ? "Per-Tier Contributions"
      : "Per-Age-Band Contributions";
  }, [structure]);

  const inner = (
    <div className="space-y-4">
      <div className="flex items-end gap-2">
        <div className="w-40">
          <label className="block text-sm font-medium mb-1">Global Type</label>
          <Select
            value={globalType}
            onValueChange={(v: any) => setGlobalType(v)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="percentage">%</SelectItem>
              <SelectItem value="dollar">$</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-40">
          <label className="block text-sm font-medium mb-1">
            Global Amount
          </label>
          <Input
            value={globalAmount}
            onChange={(e) => setGlobalAmount(e.target.value)}
            placeholder={globalType === "percentage" ? "e.g. 75" : "e.g. 200"}
          />
        </div>
        <Button variant="outline" onClick={applyGlobal}>
          Apply to All Rows
        </Button>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>
              {structure?.rateStructureType === "tier_rates"
                ? "Coverage Tier"
                : "Age Band"}
            </TableHead>
            <TableHead>Monthly Rate</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Amount</TableHead>
            {structure?.rateStructureType === "tier_rates" && (
              <>
                <TableHead>Payer</TableHead>
                <TableHead>Percent</TableHead>
              </>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.key}>
              <TableCell className="capitalize">
                {normalizeDisplayLabel(r.label)}
              </TableCell>
              <TableCell>${r.monthly}</TableCell>
              <TableCell>
                <Select
                  value={values[r.key]?.type || "percentage"}
                  onValueChange={(v: any) =>
                    setValues((prev) => ({
                      ...prev,
                      [r.key]: { type: v, amount: prev[r.key]?.amount || "" },
                    }))
                  }
                >
                  <SelectTrigger className="w-28">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">%</SelectItem>
                    <SelectItem value="dollar">$</SelectItem>
                  </SelectContent>
                </Select>
              </TableCell>
              <TableCell>
                <Input
                  value={values[r.key]?.amount || ""}
                  onChange={(e) =>
                    setValues((prev) => ({
                      ...prev,
                      [r.key]: {
                        type: prev[r.key]?.type || "percentage",
                        amount: e.target.value,
                        payer: prev[r.key]?.payer,
                        percent: prev[r.key]?.percent,
                      },
                    }))
                  }
                  placeholder={
                    values[r.key]?.type === "dollar" ? "e.g. 200" : "e.g. 75"
                  }
                />
              </TableCell>
              {structure?.rateStructureType === "tier_rates" && (
                <>
                  <TableCell>
                    <Select
                      value={values[r.key]?.payer || "employer"}
                      onValueChange={(v: any) =>
                        setValues((prev) => ({
                          ...prev,
                          [r.key]: { ...prev[r.key], payer: v },
                        }))
                      }
                    >
                      <SelectTrigger className="w-28">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="employer">Employer</SelectItem>
                        <SelectItem value="employee">Employee</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Input
                      value={values[r.key]?.percent || ""}
                      onChange={(e) =>
                        setValues((prev) => ({
                          ...prev,
                          [r.key]: { ...prev[r.key], percent: e.target.value },
                        }))
                      }
                      placeholder="e.g. 50"
                    />
                  </TableCell>
                </>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {!deferSave && (
        <div className="flex justify-end">
          <Button onClick={onSave}>Save Per-Rate Contributions</Button>
        </div>
      )}
    </div>
  );

  if (unstyled) {
    return (
      <div className="space-y-4">
        <h4 className="font-medium">{title}</h4>
        {inner}
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>
          Define contribution for each{" "}
          {structure?.rateStructureType === "tier_rates"
            ? "coverage tier"
            : "age band"}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">{inner}</CardContent>
    </Card>
  );
}
